/*
  Comportements de la page de collection.

  Le formulaire fonctionne seul : chaque case cochée et chaque tri s'envoient
  en GET et rechargent la page. Ce fichier n'ajoute que deux commodités, et
  n'invente aucun état que le balisage ne porte pas déjà :

  1. le tri et les filtres s'appliquent au changement, sans bouton d'envoi ;
  2. sous 990 px, le tiroir de filtres devient un panneau, avec les memes
     regles d'accessibilite que la navigation : Echap, focus contenu, retour du
     focus, defilement verrouille.
*/
(() => {
  const form = document.querySelector('[data-collection-form]');
  if (!form) return;

  /* ---------- Envoi au changement ---------- */

  const submit = () => {
    /*
      Une case decochee ne doit pas laisser son parametre dans l'adresse : le
      formulaire est reconstruit a partir des seuls champs renseignes.
    */
    const data = new FormData(form);
    const params = new URLSearchParams();
    for (const [name, value] of data.entries()) {
      if (String(value).trim() !== '') params.append(name, value);
    }
    const query = params.toString();
    window.location.href = query ? `${form.action}?${query}` : form.action;
  };

  for (const range of form.querySelectorAll('[data-price-range]')) {
    const maximum = Number(range.dataset.priceMax) || 0;
    const minField = range.querySelector('[data-price-input="min"]');
    const maxField = range.querySelector('[data-price-input="max"]');
    const minSlider = range.querySelector('[data-price-slider="min"]');
    const maxSlider = range.querySelector('[data-price-slider="max"]');
    if (!maximum || !minField || !maxField || !minSlider || !maxSlider) continue;

    const borner = (value) => Math.min(maximum, Math.max(0, Number(value) || 0));
    const tracer = () => {
      const minimum = borner(minSlider.value);
      const maximumCourant = borner(maxSlider.value);
      range.style.setProperty('--price-start', `${(minimum / maximum) * 100}%`);
      range.style.setProperty('--price-end', `${(maximumCourant / maximum) * 100}%`);
      minSlider.style.zIndex = minimum > maximum * 0.75 ? '2' : '1';
    };
    const ecrireChamps = () => {
      const minimum = borner(minSlider.value);
      const maximumCourant = borner(maxSlider.value);
      minField.value = minimum > 0 ? String(Math.round(minimum)) : '';
      maxField.value =
        maximumCourant < maximum ? String(Math.round(maximumCourant)) : '';
      tracer();
    };
    const lireChamps = () => {
      const minimum = minField.value === '' ? 0 : borner(minField.value);
      const maximumCourant =
        maxField.value === '' ? maximum : borner(maxField.value);
      minSlider.value = String(Math.min(minimum, maximumCourant));
      maxSlider.value = String(Math.max(minimum, maximumCourant));
      tracer();
    };

    minSlider.addEventListener('input', () => {
      if (Number(minSlider.value) > Number(maxSlider.value)) {
        minSlider.value = maxSlider.value;
      }
      ecrireChamps();
    });
    maxSlider.addEventListener('input', () => {
      if (Number(maxSlider.value) < Number(minSlider.value)) {
        maxSlider.value = minSlider.value;
      }
      ecrireChamps();
    });
    minSlider.addEventListener('change', submit);
    maxSlider.addEventListener('change', submit);
    minField.addEventListener('input', lireChamps);
    maxField.addEventListener('input', lireChamps);
    tracer();
  }

  form.addEventListener('change', (event) => {
    if (event.target.matches('input[type="checkbox"], select, [data-price-input]')) submit();
  });

  /* ---------- Panneau de filtres ---------- */

  const toggle = form.querySelector('[data-filter-toggle]');
  const panel = form.querySelector('[data-filter-panel]');
  const scrim = form.querySelector('[data-filter-scrim]');
  if (!toggle || !panel || !scrim) return;

  scrim.hidden = false;

  const FOCUSABLE = 'a[href], button:not([disabled]), select, input:not([type="hidden"]), summary';
  const desktop = window.matchMedia('(min-width: 990px)');
  let open = false;

  const focusable = () =>
    [...panel.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);

  const lockScroll = () => {
    const gap = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (gap > 0) document.body.style.paddingRight = `${gap}px`;
  };

  const unlockScroll = () => {
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  };

  const setOpen = (next) => {
    if (next === open) return;
    open = next;

    toggle.setAttribute('aria-expanded', String(open));

    if (open) {
      panel.setAttribute('data-filter-open', '');
      scrim.setAttribute('data-filter-open', '');
      lockScroll();
      const first = focusable()[0];
      if (first) first.focus();
    } else {
      panel.removeAttribute('data-filter-open');
      scrim.removeAttribute('data-filter-open');
      unlockScroll();
      toggle.focus();
    }
  };

  toggle.addEventListener('click', () => setOpen(!open));
  scrim.addEventListener('click', () => setOpen(false));
  panel.addEventListener('click', (event) => {
    if (event.target.closest('[data-filter-close]')) setOpen(false);
  });

  document.addEventListener('keydown', (event) => {
    if (!open) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      return;
    }

    if (event.key !== 'Tab') return;

    const items = focusable();
    if (items.length === 0) return;

    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || !panel.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  });

  const onBreakpoint = () => {
    if (desktop.matches) setOpen(false);
  };

  if (typeof desktop.addEventListener === 'function') {
    desktop.addEventListener('change', onBreakpoint);
  } else {
    desktop.addListener(onBreakpoint);
  }
})();
